import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProfile, useUpdateNotifications } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { Toggle } from '../components/Toggle';
import { BlueHeader, HeaderBack } from '../components/ScreenShell';
import { BellIcon, CalendarIcon, ChartIcon, ChevronRightIcon, LogoutIcon } from '../components/icons';
import { backButton, haptics } from '../telegram';
import { setThemeMode, useThemeMode, type ThemeMode } from '../theme';
import type { NotificationPrefs } from '../api/types';

const THEME_OPTIONS: { value: ThemeMode; label: string }[] = [
  { value: 'system', label: 'Системная' },
  { value: 'dark', label: 'Тёмная' },
  { value: 'light', label: 'Светлая' },
];

export function SettingsScreen() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { data, isLoading, isError, error } = useProfile(null);
  const updateNotifications = useUpdateNotifications();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const themeMode = useThemeMode();

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  useEffect(() => {
    if (data && prefs === null) setPrefs(data.notifications);
  }, [data, prefs]);

  if (isLoading || !prefs) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const toggle = (key: keyof NotificationPrefs) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    haptics.tap();
    updateNotifications.mutate(next, {
      onError: () => setPrefs(prefs), // revert
    });
  };

  const logout = () => {
    haptics.tap();
    auth.logout();
    navigate('/');
  };

  const rows = [
    { key: 'push' as const, label: 'Push-уведомления', Icon: BellIcon },
    { key: 'tournaments' as const, label: 'Анонсы турниров', Icon: CalendarIcon },
    { key: 'rating' as const, label: 'Изменения в рейтинге', Icon: ChartIcon },
  ];

  return (
    <div>
      <BlueHeader>
        <div className="bhead-top" style={{ marginBottom: 0 }}>
          <HeaderBack onClick={() => navigate(-1)} />
          <div className="bhead-title bhead-title--sm" style={{ flex: 1 }}>
            Настройки
          </div>
        </div>
      </BlueHeader>

      <div className="content">
      <Link to="/settings/edit" className="settings-profile">
        <Avatar name={data.displayName} src={data.photoUrl} size={50} bordered />
        <div className="info">
          <div className="n">{data.displayName}</div>
          <div className="s">Изменить профиль и фото</div>
        </div>
        <span className="chev">
          <ChevronRightIcon />
        </span>
      </Link>

      <div className="group-label">Уведомления</div>
      <div className="list-card">
        {rows.map(({ key, label, Icon }) => (
          <div className="setting-row" key={key}>
            <span className="setting-ico">
              <Icon />
            </span>
            <span className="lbl">{label}</span>
            <Toggle checked={prefs[key]} onChange={() => toggle(key)} label={label} />
          </div>
        ))}
      </div>

      <div className="group-label" style={{ marginTop: 22 }}>
        Клуб
      </div>
      <div className="list-card">
        <Link to="/faq" className="setting-row" style={{ textDecoration: 'none', color: 'inherit' }}>
          <span className="setting-ico">?</span>
          <span className="lbl">Вопросы и ответы</span>
          <span className="chev">
            <ChevronRightIcon />
          </span>
        </Link>
      </div>

      <div className="group-label" style={{ marginTop: 22 }}>
        Оформление
      </div>
      <div className="segmented" role="radiogroup" aria-label="Тема оформления">
        {THEME_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            className={themeMode === value ? 'active' : ''}
            role="radio"
            aria-checked={themeMode === value}
            onClick={() => {
              haptics.tap();
              setThemeMode(value);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <button className="btn btn--soft-danger" style={{ marginTop: 22 }} onClick={logout}>
        <LogoutIcon />
        Выйти из аккаунта
      </button>
      </div>
    </div>
  );
}
